import type { AppAbility } from "@pbz/permission/index";

export interface RoutePermission {
  action: string;
  subject: string;
}

export interface RoutePermissionsConfig {
  [route: string]: RoutePermission[];
}

// Routes with required permissions using CASL action/subject format
export const ROUTE_PERMISSIONS: RoutePermissionsConfig = {
  "/dashboard": [{ action: "READ", subject: "DASHBOARD" }],
  "/users": [{ action: "LIST", subject: "USER" }],
  "/lead/create": [{ action: "CREATE", subject: "LEAD" }],
  "/lead/list": [{ action: "LIST", subject: "LEAD" }],
  "/lead/edit": [{ action: "UPDATE", subject: "LEAD" }],
  // Add more routes as needed
};

/**
 * Check if user has permission to access a specific route
 * @param ability - CASL ability instance
 * @param route - Route path to check
 * @returns boolean indicating if user has permission
 */
export function checkRoutePermission(
  ability: AppAbility,
  route: string,
): boolean {
  const requiredPermissions = getRoutePermissions(route);

  if (!requiredPermissions || requiredPermissions.length === 0) {
    // No permissions required for this route
    return true;
  }

  // Check if user has ANY of the required permissions
  return requiredPermissions.some(({ action, subject }) =>
    ability.can(action, subject),
  );
}

/**
 * Get required permissions for a specific route
 * @param route - Route path
 * @returns Array of required permissions or null if no permissions required
 */
export function getRoutePermissions(route: string): RoutePermission[] | null {
  // Direct match
  if (ROUTE_PERMISSIONS[route]) {
    return ROUTE_PERMISSIONS[route];
  }

  // Check for nested routes (e.g., /lead/create/step1 should match /lead/create)
  const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find((definedRoute) => {
    // Exact match
    if (definedRoute === route) {
      return true;
    }

    // Check if current path starts with a route and is followed by / or end of string
    // This handles nested routes like /lead/create/step2
    if (
      route.startsWith(definedRoute) &&
      (route.length === definedRoute.length ||
        route[definedRoute.length] === "/")
    ) {
      return true;
    }

    return false;
  });

  return matchingRoute ? (ROUTE_PERMISSIONS[matchingRoute] ?? null) : null;
}

/**
 * Get all routes that require a specific permission
 * @param action - CASL action
 * @param subject - CASL subject
 * @returns Array of route paths that require this permission
 */
export function getRoutesForPermission(
  action: string,
  subject: string,
): string[] {
  return Object.entries(ROUTE_PERMISSIONS)
    .filter(([, permissions]) =>
      permissions.some(
        (permission) =>
          permission.action === action && permission.subject === subject,
      ),
    )
    .map(([route]) => route);
}
